import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export default function MeshPreviewViewer({ url, style, onBlobReady }) {
  const mountRef = useRef(null);
  const cleanupRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [progress, setProgress] = useState(0);   // 0-50: download, 51-100: parse+render

  useEffect(() => {
    if (!url || !mountRef.current) return;
    setLoading(true);
    setLoadError(false);
    setProgress(0);

    let cancelled = false;
    let blobUrl = null;

    const container = mountRef.current;

    // ── Three.js scene setup (done once, reused regardless of fetch path) ──
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x080808);

    const camera = new THREE.PerspectiveCamera(45, (container.clientWidth || 400) / (container.clientHeight || 300), 0.01, 1000);
    camera.position.set(0, 1, 3);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(container.clientWidth || 400, container.clientHeight || 300);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    container.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 1.2));
    const dirLight = new THREE.DirectionalLight(0xffffff, 2);
    dirLight.position.set(5, 10, 7);
    scene.add(dirLight);
    const fillLight = new THREE.DirectionalLight(0x8899ff, 0.5);
    fillLight.position.set(-5, 3, -5);
    scene.add(fillLight);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1.2;

    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    let animFrameId;

    const onModelReady = (gltf) => {
      if (cancelled) return;
      const model = gltf.scene;

      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const scale = 2 / maxDim;

      model.scale.setScalar(scale);
      model.position.sub(center.multiplyScalar(scale));
      scene.add(model);

      const fitDist = maxDim * scale * 1.5;
      camera.position.set(fitDist, fitDist * 0.6, fitDist);
      camera.lookAt(0, 0, 0);
      controls.target.set(0, 0, 0);
      controls.update();

      setLoading(false);

      const animate = () => {
        animFrameId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();
    };

    const onModelError = (err) => {
      if (cancelled) return;
      console.error('MeshPreviewViewer load error', err);
      setLoading(false);
      setLoadError(true);
    };

    const loadViaBlobFetch = async () => {
      try {
        // If caller already has a blob URL (cached from a previous load), skip download entirely
        if (url.startsWith('blob:')) {
          if (cancelled) return;
          setProgress(95);
          loader.load(url, onModelReady, undefined, onModelError);
          return;
        }

        // Don't send Authorization to external CDN URLs — it triggers a CORS preflight
        // that CloudFront doesn't handle. Files on CloudFront are already public.
        const isExternal = /^https?:\/\/(?!localhost)/.test(url);
        const token = localStorage.getItem('token') || '';
        const headers = isExternal ? {} : (token ? { Authorization: `Bearer ${token}` } : {});
        const res = await fetch(url, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        // Stream response so we can show real download progress
        const contentLength = Number(res.headers.get('content-length')) || 0;
        const reader = res.body.getReader();
        const chunks = [];
        let received = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (cancelled) return;
          chunks.push(value);
          received += value.length;
          if (contentLength > 0) {
            // First 90% = download, last 10% reserved for parse
            setProgress(Math.min(90, Math.round((received / contentLength) * 90)));
          }
        }

        if (cancelled) return;
        setProgress(95); // parse starting

        const blob = new Blob(chunks, { type: 'model/gltf-binary' });
        blobUrl = URL.createObjectURL(blob);

        if (onBlobReady) onBlobReady(blobUrl); // let parent cache it
        loader.load(blobUrl, onModelReady, undefined, onModelError);
      } catch (err) {
        if (!cancelled) {
          console.error('MeshPreviewViewer fetch error', err);
          setLoading(false);
          setLoadError(true);
        }
      }
    };

    loadViaBlobFetch();

    // Resize observer
    const resizeObserver = new ResizeObserver(() => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    resizeObserver.observe(container);

    cleanupRef.current = () => {
      cancelled = true;
      cancelAnimationFrame(animFrameId);
      resizeObserver.disconnect();
      controls.dispose();
      renderer.dispose();
      dracoLoader.dispose();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };

    return cleanupRef.current;
  }, [url]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
      <div ref={mountRef} style={{ width: '100%', height: '100%' }} />
      {loading && !loadError && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          background: 'rgba(8,8,8,0.88)',
          gap: '10px',
        }}>
          <svg style={{ width: 24, height: 24, animation: 'spin 1s linear infinite', color: '#60a5fa' }}
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          {progress > 0 ? (
            <div style={{ width: 120, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ width: '100%', height: 3, background: '#3f3f46', borderRadius: 2 }}>
                <div style={{ width: `${progress}%`, height: '100%', background: '#60a5fa', borderRadius: 2, transition: 'width 0.2s' }} />
              </div>
              <span style={{ fontSize: 10, color: '#9ca3af', textAlign: 'center' }}>
                {progress < 95 ? `${progress}% downloading` : 'Parsing…'}
              </span>
            </div>
          ) : (
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Loading 3D…</span>
          )}
        </div>
      )}
      {loadError && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(8,8,8,0.85)',
        }}>
          <span style={{ fontSize: 11, color: '#f87171' }}>Failed to load model</span>
        </div>
      )}
    </div>
  );
}
